import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { favoritesAPI } from '../services/api';
import { useCustomerAuth } from './CustomerAuthContext';

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(false);
    const { customer } = useCustomerAuth();

    const fetchFavorites = useCallback(async () => {
        if (!customer) {
            setFavoriteIds(new Set());
            setFavorites([]);
            return;
        }
        try {
            const res = await favoritesAPI.getAll();
            const data = res.data || [];
            setFavorites(data);
            setFavoriteIds(new Set(data.map(f => f.productId)));
        } catch (e) { /* ignore */ }
    }, [customer]);

    useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

    const toggleFavorite = async (productId) => {
        if (!customer) return { needsLogin: true };
        setLoading(true);
        try {
            const res = await favoritesAPI.toggle(productId);
            const newIds = new Set(favoriteIds);
            if (res.data.favorited) {
                newIds.add(productId);
            } else {
                newIds.delete(productId);
            }
            setFavoriteIds(newIds);
            await fetchFavorites();
            return res.data;
        } catch (e) {
            return { error: true };
        } finally {
            setLoading(false);
        }
    };

    const isFavorited = (productId) => favoriteIds.has(productId);

    return (
        <FavoritesContext.Provider value={{
            favorites, favoriteIds, loading,
            toggleFavorite, isFavorited, fetchFavorites
        }}>
            {children}
        </FavoritesContext.Provider>
    );
};

const defaultValue = {
    favorites: [], favoriteIds: new Set(), loading: false,
    toggleFavorite: () => ({ needsLogin: true }),
    isFavorited: () => false,
    fetchFavorites: () => {}
};
export const useFavorites = () => useContext(FavoritesContext) || defaultValue;
